import { useState } from "react";
import { useQuery, useAction } from "convex/react";
import { useNavigate } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Users, LayoutGrid, Car, Shield, Bell, BellOff, Loader2, Smartphone, Send } from "lucide-react";
import { usePushNotifications } from "@/hooks/use-push-notifications";
import { useToast } from "@/hooks/use-toast";

// Deep link options for the dropdown
const DEEP_LINK_OPTIONS = [
  { value: "none", label: "None" },
  { value: "/", label: "Home" },
  { value: "/insights", label: "Insights" },
  { value: "/settings", label: "Settings" },
  { value: "/blocks", label: "Blocks" },
  { value: "/expenses", label: "Expenses" },
];

export function AdminPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isAdmin = useQuery(api.admin.isAdmin);
  const stats = useQuery(api.admin.getStats);
  const usersWithTokens = useQuery(api.pushNotifications.getUsersWithTokens);
  const sendAdminNotification = useAction(api.pushNotifications.sendAdminNotification);

  // Send notification form state
  const [targetType, setTargetType] = useState<"all" | "user">("all");
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [notifTitle, setNotifTitle] = useState("");
  const [notifBody, setNotifBody] = useState("");
  const [deepLink, setDeepLink] = useState("none");
  const [isSending, setIsSending] = useState(false);

  const {
    isSupported: pushSupported,
    isEnabled: pushEnabled,
    isRegistering,
    permissionStatus,
    tokens,
    register: registerPush,
    unregister: unregisterPush,
  } = usePushNotifications();

  // Calculate device count for send button
  const getDeviceCount = () => {
    if (targetType === "all") {
      return usersWithTokens?.reduce((sum, u) => sum + u.tokenCount, 0) ?? 0;
    }
    const user = usersWithTokens?.find((u) => u._id === selectedUserId);
    return user?.tokenCount ?? 0;
  };

  const handleSendNotification = async () => {
    if (!notifTitle.trim() || !notifBody.trim()) {
      toast({ title: "Title and body are required", variant: "destructive" });
      return;
    }
    if (targetType === "user" && !selectedUserId) {
      toast({ title: "Please select a user", variant: "destructive" });
      return;
    }

    setIsSending(true);
    try {
      const result = await sendAdminNotification({
        targetType,
        userId: targetType === "user" ? selectedUserId as Id<"users"> : undefined,
        title: notifTitle.trim(),
        body: notifBody.trim(),
        deepLink: deepLink !== "none" ? deepLink : undefined,
      });

      if (result.success) {
        toast({ title: `Notification sent to ${result.deviceCount} device${result.deviceCount === 1 ? "" : "s"}` });
        // Reset form
        setNotifTitle("");
        setNotifBody("");
        setDeepLink("none");
      } else {
        toast({ title: result.error ?? "Failed to send notification", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Failed to send notification", variant: "destructive" });
      console.error(error);
    } finally {
      setIsSending(false);
    }
  };

  // Loading state
  if (isAdmin === undefined) {
    return (
      <div className="min-h-screen bg-background">
        <div className="sticky top-0 z-10 bg-background border-b pt-[env(safe-area-inset-top)]">
          <div className="flex items-center gap-3 p-4 max-w-4xl mx-auto">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-semibold">Admin Panel</h1>
          </div>
        </div>
        <div className="p-4 max-w-4xl mx-auto">
          <div className="h-40 bg-muted animate-pulse rounded-lg" />
        </div>
      </div>
    );
  }

  // Not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <Shield className="h-6 w-6" />
              <CardTitle>Access Denied</CardTitle>
            </div>
            <CardDescription>
              You don&apos;t have permission to access the admin panel.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/")} className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b pt-[env(safe-area-inset-top)]">
        <div className="flex items-center gap-3 p-4 max-w-4xl mx-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold">Admin Panel</h1>
            <p className="text-sm text-muted-foreground">System overview and management</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6 max-w-4xl mx-auto">
        {/* Stats Overview */}
        <Card>
          <CardHeader>
            <CardTitle>System Stats</CardTitle>
            <CardDescription>Overview of all data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Users className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{stats?.totalUsers ?? "-"}</p>
                <p className="text-sm text-muted-foreground">Users</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <LayoutGrid className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{stats?.totalBlocks ?? "-"}</p>
                <p className="text-sm text-muted-foreground">Blocks</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Car className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{stats?.totalShifts ?? "-"}</p>
                <p className="text-sm text-muted-foreground">Shifts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Push Notifications */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              <CardTitle>Push Notifications</CardTitle>
            </div>
            <CardDescription>
              Get notified 15 minutes before your scheduled blocks start
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!pushSupported ? (
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <Smartphone className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Not Available</p>
                  <p className="text-sm text-muted-foreground">
                    Push notifications require the native iOS or Android app
                  </p>
                </div>
              </div>
            ) : pushEnabled ? (
              <>
                <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <Bell className="h-5 w-5 text-green-600" />
                  <div className="flex-1">
                    <p className="font-medium text-green-600">Notifications Enabled</p>
                    <p className="text-sm text-muted-foreground">
                      {tokens?.length === 1
                        ? "1 device registered"
                        : `${tokens?.length ?? 0} devices registered`}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => unregisterPush()}
                >
                  <BellOff className="h-4 w-4 mr-2" />
                  Disable Notifications
                </Button>
              </>
            ) : (
              <>
                {permissionStatus === "denied" ? (
                  <div className="flex items-center gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <BellOff className="h-5 w-5 text-destructive" />
                    <div>
                      <p className="font-medium text-destructive">Permission Denied</p>
                      <p className="text-sm text-muted-foreground">
                        Enable notifications in your device settings
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                    <BellOff className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Notifications Disabled</p>
                      <p className="text-sm text-muted-foreground">
                        Enable to get block reminders
                      </p>
                    </div>
                  </div>
                )}
                <Button
                  className="w-full"
                  onClick={() => registerPush()}
                  disabled={isRegistering || permissionStatus === "denied"}
                >
                  {isRegistering ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Enabling...
                    </>
                  ) : (
                    <>
                      <Bell className="h-4 w-4 mr-2" />
                      Enable Notifications
                    </>
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Send Push Notification */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              <CardTitle>Send Push Notification</CardTitle>
            </div>
            <CardDescription>
              Send notifications to all users or a specific user
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Target Type */}
            <div className="space-y-2">
              <Label>Send to</Label>
              <Select value={targetType} onValueChange={(v) => setTargetType(v as "all" | "user")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users ({usersWithTokens?.reduce((sum, u) => sum + u.tokenCount, 0) ?? 0} devices)</SelectItem>
                  <SelectItem value="user">Individual User</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* User Select (only if individual) */}
            {targetType === "user" && (
              <div className="space-y-2">
                <Label>Select User</Label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a user..." />
                  </SelectTrigger>
                  <SelectContent>
                    {usersWithTokens?.map((user) => (
                      <SelectItem key={user._id} value={user._id}>
                        {user.name} ({user.tokenCount} device{user.tokenCount === 1 ? "" : "s"})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="notif-title">Title</Label>
              <Input
                id="notif-title"
                placeholder="Notification title"
                value={notifTitle}
                onChange={(e) => setNotifTitle(e.target.value)}
              />
            </div>

            {/* Body */}
            <div className="space-y-2">
              <Label htmlFor="notif-body">Message</Label>
              <Textarea
                id="notif-body"
                placeholder="Notification message"
                value={notifBody}
                onChange={(e) => setNotifBody(e.target.value)}
                rows={3}
              />
            </div>

            {/* Deep Link */}
            <div className="space-y-2">
              <Label>Open Screen (optional)</Label>
              <Select value={deepLink} onValueChange={setDeepLink}>
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  {DEEP_LINK_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Send Button */}
            <Button
              className="w-full"
              onClick={handleSendNotification}
              disabled={isSending || !notifTitle.trim() || !notifBody.trim() || (targetType === "user" && !selectedUserId)}
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send to {getDeviceCount()} device{getDeviceCount() === 1 ? "" : "s"}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Placeholder sections for future features */}
        <Card>
          <CardHeader>
            <CardTitle>User Management</CardTitle>
            <CardDescription>Coming soon</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">User management features will be added here.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>System Settings</CardTitle>
            <CardDescription>Coming soon</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">System-wide settings will be added here.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Analytics</CardTitle>
            <CardDescription>Coming soon</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Usage analytics and reports will be added here.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
